import React, { useState } from "react";
import PropTypes from "prop-types";
import { useMutation } from "@apollo/client";
import classNames from "classnames";
import ShowTimeInHoursAndMins from "components/ShowTimeInHoursAndMins";
import { COMPLETE_TASK, makeCompleteTaskOptions } from "./completeTaskMutation";

const RoadmapTaskItem = ({
  item,
  halfDay,
  isReordering,
  updatePosition,
  isLastItem,
  roadmapCode,
}) => {
  const [smsSentCount, setSmsSentCount] = useState(null);
  const [showSmsNotification, setShowSmsNotification] = useState(false);

  const [completeTask, { loading: completing }] = useMutation(COMPLETE_TASK, {
    onCompleted: (data) => {
      if (data?.completeTask?.success) {
        const count = data.completeTask.sms_sent_count || 0;
        setSmsSentCount(count);
        setShowSmsNotification(true);
        // Hide notification after 5 seconds
        setTimeout(() => setShowSmsNotification(false), 5000);
      }
    },
    onError: (error) => {
      console.error("Error completing task:", error);
      alert("Erreur lors de la complétion de la tâche");
    },
  });
  const normalizedCategory = String(item.category || "")
    .trim()
    .toLowerCase()
    .replace(/[_\s-]+/g, "");

  const isBreak = normalizedCategory === "break";
  const isFakeOrder = normalizedCategory === "fakeorder";
  const isCompleted = Boolean(item.is_completed);

  const addressLabel = String(item.address_label || "").trim();

  // Parse coordinates for navigation
  const getCoordinates = () => {
    if (!item.address_coordinates) return null;
    const [lat, lng] = item.address_coordinates.split(",").map((c) => c.trim());
    return { lat: parseFloat(lat), lng: parseFloat(lng) };
  };

  const coordinates = getCoordinates();

  const handleCompleteTask = () => {
    if (!roadmapCode || !item.taskId) {
      console.error("Missing roadmapCode or taskId");
      return;
    }
    completeTask(makeCompleteTaskOptions(roadmapCode, item.taskId));
  };

  const itemClasses = classNames("round-delivery-item", {
    "-completed": isCompleted,
    "-break": isBreak,
    "-fakeorder": isFakeOrder,
  });

  return (
    <div className="delivery-item">
      <div className={itemClasses}>
        {isReordering && (
          <div>
            {Number(item.position) !== 1 && (
              <button
                type="button"
                className="reorder -up button button-outline"
                onClick={updatePosition({
                  halfDay,
                  item,
                  direction: "UP",
                })}
              >
                <i className="fa fa-arrow-up" aria-hidden="true" />
              </button>
            )}
            {!isLastItem && (
              <button
                type="button"
                className="reorder -down button button-outline"
                onClick={updatePosition({
                  halfDay,
                  item,
                  direction: "DOWN",
                })}
              >
                <i className="fa fa-arrow-down" aria-hidden="true" />
              </button>
            )}
          </div>
        )}

        <div className="row">
          <div className="column column-50">
            <div>
              <span className="label">Type</span> :{" "}
              {isBreak ? "Pause" : "Point de passage"}
            </div>
          </div>
          <div className="column column-50">
            <div>
              <span className="label">Référence</span> :{" "}
              {item.comment || (isBreak ? "Pause" : "Passage")}
              {isCompleted && (
                <i
                  className="fa fa-check-circle"
                  title="Complété"
                  style={{ color: "#27ae60", marginLeft: "0.5rem" }}
                />
              )}
            </div>
          </div>
        </div>

        {/* SMS notification banner */}
        {showSmsNotification && smsSentCount !== null && (
          <div className="task-sms-notification">
            <i className="fa fa-envelope" />
            <span>
              {smsSentCount > 0
                ? `${smsSentCount} SMS envoyé${smsSentCount > 1 ? "s" : ""}`
                : "Aucun SMS envoyé"}
            </span>
          </div>
        )}

        {/* Address label for FakeOrder */}
        {isFakeOrder && (
          <div className="row">
            <div className="column">
              <span className="label">Adresse</span> : {addressLabel || "-"}
            </div>
          </div>
        )}

        <div className="delivery-options">
          {/* Service time icon */}
          {item.service_time != null && item.service_time > 0 && (
            <i className="fa fa-clock" title="Durée de service">
              {" "}
              {ShowTimeInHoursAndMins({
                // service_time for Break is provided in seconds; convert to minutes
                time: Math.round(item.service_time / 60),
                returnText: true,
              })}
            </i>
          )}

          {/* Road time and distance */}
          {item.road_time != null && item.road_time > 0 && (
            <i className="fas fa-road road-time-icon" title="Temps de conduite">
              {" "}
              {ShowTimeInHoursAndMins({
                time: item.road_time / 60,
                returnText: true,
              })}{" "}
              - {(item.road_distance / 1000).toFixed(1)}km
            </i>
          )}

          {/* Break icon */}
          {isBreak && (
            <i
              className="fa fa-coffee"
              title="Pause"
              style={{ color: "#8b4513" }}
            ></i>
          )}

          {/* FakeOrder/Passage icon */}
          {isFakeOrder && (
            <i
              className="fa fa-map-marker-alt"
              title="Point de passage"
              style={{ color: "#3498db" }}
            ></i>
          )}
        </div>

        <div className="delivery-item__actions">
          <div className="delivery-item__actions-left">
            {/* Complete button for tasks */}
            {!isCompleted && !isReordering && (
              <button
                type="button"
                className="button button-small task-complete-btn"
                onClick={handleCompleteTask}
                disabled={completing}
              >
                {completing ? (
                  <>
                    <i className="fa fa-spinner fa-spin" /> Complétion...
                  </>
                ) : (
                  <>
                    <i className="fa fa-check" /> Compléter
                  </>
                )}
              </button>
            )}
            {isCompleted && (
              <span className="task-completed-badge">
                <i className="fa fa-check-circle" /> Complété
                {item.completed_at && (
                  <span className="task-completed-time">
                    {" "}
                    à{" "}
                    {new Date(item.completed_at).toLocaleTimeString("fr-FR", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                )}
              </span>
            )}
          </div>

          {/* Waze and Google Maps buttons for FakeOrder */}
          {isFakeOrder && coordinates && (
            <div className="delivery-item__actions-right">
              <span className="tiny-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 512 512"
                  width="24"
                  fill="#20274a"
                  height="24"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    window.open(
                      `waze://?ll=${coordinates.lat},${coordinates.lng}&navigate=yes`,
                      "_blank"
                    );
                  }}
                  style={{ cursor: "pointer" }}
                  title="Navigate with Waze"
                >
                  <path d="M502.2 201.7C516.7 287.5 471.2 369.6 389 409.8c13 34.1-12.4 70.2-48.3 70.2a51.7 51.7 0 0 1 -51.6-49c-6.4 .2-64.2 0-76.3-.6A51.7 51.7 0 0 1 159 479.9c-33.9-1.4-58-34.8-47-67.9-37.2-13.1-72.5-34.9-99.6-70.8-13-17.3-.5-41.8 20.8-41.8 46.3 0 32.2-54.2 43.2-110.3C94.8 95.2 193.1 32 288.1 32c102.5 0 197.2 70.7 214.1 169.7zM373.5 388.3c42-19.2 81.3-56.7 96.3-102.1 40.5-123.1-64.2-228-181.7-228-83.5 0-170.3 55.4-186.1 136-9.5 48.9 5 131.4-68.8 131.4C58.2 358.6 91.6 378.1 127 389.5c24.7-21.8 63.9-15.5 79.8 14.3 14.2 1 79.2 1.2 87.9 .8a51.7 51.7 0 0 1 78.8-16.4zM205.1 187.1c0-34.7 50.8-34.8 50.8 0s-50.8 34.7-50.8 0zm116.6 0c0-34.7 50.9-34.8 50.9 0s-50.9 34.8-50.9 0zm-122.6 70.7c-3.4-16.9 22.2-22.2 25.6-5.2l.1 .3c4.1 21.4 29.9 44 64.1 43.1 35.7-.9 59.3-22.2 64.1-42.8 4.5-16.1 28.6-10.4 25.5 6-5.2 22.2-31.2 62-91.5 62.9-42.6 0-80.9-27.8-87.9-64.3z" />
                </svg>
              </span>
              <span className="tiny-icon">
                <svg
                  id="Layer_1"
                  data-name="Layer 1"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 230.92 331"
                  width="24"
                  height="24"
                  fill="#20274a"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    window.open(
                      `https://www.google.com/maps/dir/?api=1&destination=${coordinates.lat},${coordinates.lng}&travelmode=driving&dir_action=navigate`,
                      "_blank"
                    );
                  }}
                  style={{ cursor: "pointer" }}
                  title="Navigate with Google Maps"
                >
                  <path
                    d="M418.92,223.86a115.69,115.69,0,0,0-123.33,35.86l54.55,45.86Z"
                    transform="translate(-268.54 -218.5)"
                    style={{ fill: "#1a73e8" }}
                  />
                  <path
                    d="M295.58,259.72a115.07,115.07,0,0,0-27,74.21c0,21.69,4.31,39.33,11.43,55.07l70.16-83.42Z"
                    transform="translate(-268.54 -218.5)"
                    style={{ fill: "#ea4335" }}
                  />
                  <path
                    d="M384,289.83a44.17,44.17,0,0,1,33.71,72.71l68.72-81.72a115.52,115.52,0,0,0-67.61-56.9l-68.66,81.72A44,44,0,0,1,384,289.83"
                    transform="translate(-268.54 -218.5)"
                    style={{ fill: "#4285f4" }}
                  />
                  <path
                    d="M384,378.15a44.14,44.14,0,0,1-33.84-72.51L280,389.06c12,26.59,31.94,47.95,52.46,74.86l85.31-101.38A44.11,44.11,0,0,1,384,378.15"
                    transform="translate(-268.54 -218.5)"
                    style={{ fill: "#fbbc04" }}
                  />
                  <path
                    d="M416,491.56c38.54-60.23,83.42-87.6,83.42-157.63a115,115,0,0,0-13-53.24l-154,183.24c6.53,8.56,13.13,17.64,19.53,27.7,23.39,36.19,16.92,57.88,32,57.88s8.62-21.75,32-57.94"
                    transform="translate(-268.54 -218.5)"
                    style={{ fill: "#34a853" }}
                  />
                </svg>
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

RoadmapTaskItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    taskId: PropTypes.string,
    category: PropTypes.string.isRequired,
    comment: PropTypes.string,
    round_line_position: PropTypes.number.isRequired,
    arrival_time: PropTypes.number,
    road_time: PropTypes.number,
    road_distance: PropTypes.number,
    service_time: PropTypes.number,
    address_label: PropTypes.string,
    address_coordinates: PropTypes.string,
    position: PropTypes.string,
    is_completed: PropTypes.bool,
    completed_at: PropTypes.string,
  }).isRequired,
  halfDay: PropTypes.string.isRequired,
  updatePosition: PropTypes.func.isRequired,
  isReordering: PropTypes.bool.isRequired,
  isLastItem: PropTypes.bool.isRequired,
  roadmapCode: PropTypes.string.isRequired,
};

export default RoadmapTaskItem;
